import React, { Fragment, FunctionComponent, useState } from 'react';
// import { ChatContext } from '../contexts/ChatContext';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import CancelIcon from '@material-ui/icons/Cancel';
import Modify from '@material-ui/icons/EditOutlined';
import ShippingIcon from '@material-ui/icons/LocalShipping';
import PauseIcon from '@material-ui/icons/Pause';
import PersonIcon from '@material-ui/icons/PersonOutline';
import ResetIcon from '@material-ui/icons/RotateLeft';
import ScheduleIcon from '@material-ui/icons/Schedule';
import StoreIcon from '@material-ui/icons/Storefront';
import clsx from 'clsx';
import { useOrdersContext } from 'contexts/OrdersContext';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import * as store from 'store';
import { OrderCustomer } from 'types';
import { assignOrder, cancelOrder, getCustomerDetails, getOrderActivityHistory, holdOrder, postToLoop } from '../api';
import { usePermissions } from '../hooks/usePermissions';
import { OrderTypes, SpotlightPermission } from '../types/enums';
import ChangeStore from './ChangeStore';
import RescheduleOrder from './RescheduleOrder';
import ResetOrder from './ResetOrder';
import { createToastMessage } from './toast';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: theme.spacing(2),
      backgroundColor: '#F4F4F4',
      verticalAlign: 'middle',
      maxHeight: '100%',
      flexWrap: 'wrap',
    },

    button: {
      height: 45,
      margin: theme.spacing(1),
      borderRadius: theme.spacing(1),
      fontWeight: 450,
    },

    wrapper: {
      float: 'right',
      margin: theme.spacing(3),
      marginLeft: 'auto',
    },

    backButton: {
      color: '#21212190',
      background: '#BDBDBD70',
      border: 'none',
    },

    colorGreen: {
      color: 'white',
      background: theme.palette.success.dark,
      border: 'none',
      '&:hover': {
        color: theme.palette.success.dark,
        border: `1px solid ${theme.palette.success.dark}`,
        transform: 'scale(1.0)',
      },
    },

    colorBlue: {
      color: 'white',
      background: theme.palette.info.dark,
      border: 'none',
      '&:hover': {
        color: theme.palette.info.dark,
        border: `1px solid ${theme.palette.info.dark}`,
        transform: 'scale(1.0)',
      },
    },

    colorOrange: {
      color: 'white',
      background: theme.palette.warning.dark,
      border: 'none',
      '&:hover': {
        color: theme.palette.warning.dark,
        border: `1px solid ${theme.palette.warning.dark}`,
        transform: 'scale(1.0)',
      },
    },

    colorRed: {
      color: 'white',
      background: theme.palette.error.dark,
      border: 'none',
      '&:hover': {
        color: theme.palette.error.dark,
        border: `1px solid ${theme.palette.error.dark}`,
        transform: 'scale(1.0)',
      },
    },
  })
);

interface ActionButtonsProps {
  orderID: string;
  shopper: string;
  onModifyOrder?: () => void;
}

interface ActionButton {
  label: string;
  icon: JSX.Element;
  color?: string;
  action: () => void;
}

//  Un-comment Chat when chat it ready

const OrderActionButtons: FunctionComponent<ActionButtonsProps> = (props) => {
  const { orderID, shopper } = props;

  // const { setChannel, setAssigned, setCurrentOrderID } = useContext(ChatContext);
  const [openChangeStore, setOpenChangeStore] = useState(false);
  const [openReschedule, setOpenReschedule] = useState(false);
  const [openResetOrder, setOpenResetOrder] = useState(false);

  const assignOrderMutation = useMutation(assignOrder);
  const cancelOrderMutation = useMutation(cancelOrder);
  const postToLoopMutation = useMutation(postToLoop);
  const holdOrderMutation = useMutation(holdOrder);

  const { orders, result } = useOrdersContext();
  const { isPermitted } = usePermissions();
  const history = useHistory();
  const classes = useStyles();

  const accessToken = store.get('spotlightAccessToken');

  const handleCloseChangeStore = () => setOpenChangeStore(false);
  const handleOpenChangeStore = () => setOpenChangeStore(true);
  const handleCloseReschedule = () => setOpenReschedule(false);
  const handleOpenReschedule = () => setOpenReschedule(true);
  const handleOpenResetOrder = () => setOpenResetOrder(true);
  const handleCloseResetOrder = () => setOpenResetOrder(false);

  const { refetch: refetchActivityHistory } = useQuery(['activityHistory', accessToken, orderID], () =>
    getOrderActivityHistory({ accessToken, orderID })
  );

  const { isLoading, data: customerData } = useQuery(['customerDetails', accessToken, orderID], () =>
    getCustomerDetails({ accessToken, orderID })
  );

  const customer: OrderCustomer = customerData?.data.details[0];

  const order = orders.find(
    (o) => o.orderID === orderID && o.firstName === customer?.customerName && o?.shopper === customer?.shopperName
  );

  const userCanResetOrder = isPermitted(
    [SpotlightPermission.RESET_ORDER, SpotlightPermission.RESET_ORDER_HIGH_RISK, SpotlightPermission.RESET_ORDER_ADMIN],
    'any'
  );
  const isWalmartOrder = Number(order?.orderTypeID ?? 0) === OrderTypes.WALMART;
  const userCanCreateDelivery = isPermitted([SpotlightPermission.CREATE_PICUP]);
  const userCanDelete = isPermitted([SpotlightPermission.DELETE_ORDER]);
  const userCanEdit = isPermitted([SpotlightPermission.EDIT_ORDER]);

  const userCanCancelOrder = orders.length && !isWalmartOrder && userCanDelete;

  const handleAssignOrder = () => {
    if (accessToken) {
      assignOrderMutation.mutate(
        { orderID, accessToken },
        {
          onSuccess: (data) => {
            createToastMessage({ message: data.data.message, error: false });
            refetchActivityHistory();
          },
          onError: (error) => {
            createToastMessage({ message: error, error: true });
          },
        }
      );
    }
  };

  const handleHoldOrder = () => {
    if (accessToken) {
      holdOrderMutation.mutate(
        { orderID, accessToken },
        {
          onSuccess: (data) => {
            createToastMessage({ message: data.data.message, error: false });
            refetchActivityHistory();
          },
          onError: (error) => {
            createToastMessage({ message: error, error: true });
          },
        }
      );
    }
  };

  const handleLoop = () => {
    if (accessToken) {
      postToLoopMutation.mutate(
        { orderID, accessToken },
        {
          onSuccess: (data) => {
            createToastMessage({ message: data.data.message, error: false });
            refetchActivityHistory();
          },
          onError: (error) => {
            createToastMessage({ message: error, error: true });
          },
        }
      );
    }
  };

  const handleCancelOrder = () => {
    if (accessToken) {
      cancelOrderMutation.mutate(
        { orderID, accessToken },
        {
          onSuccess: (data) => {
            createToastMessage({ message: data.data.message, error: false });
            refetchActivityHistory();
          },
          onError: (error) => {
            createToastMessage({ message: error, error: true });
          },
        }
      );
    }
  };

  const handleModifyOrder = () => {
    props.onModifyOrder?.();
  };

  const actionButtons: ActionButton[] = [
    { label: 'Assign to me', icon: <PersonIcon />, color: classes.colorGreen, action: handleAssignOrder },
    ...(userCanEdit ? [{ label: 'Modify Order', icon: <Modify color="primary" />, action: handleModifyOrder }] : []),
    { label: 'Reschedule', icon: <ScheduleIcon color="primary" />, action: handleOpenReschedule },
    ...(userCanEdit
      ? [{ label: 'Change Store', icon: <StoreIcon color="primary" />, action: handleOpenChangeStore }]
      : []),
    ...(userCanCreateDelivery
      ? [{ label: 'Post to Loop', icon: <ShippingIcon color="primary" />, action: handleLoop }]
      : []),
    { label: 'Hold Order', icon: <PauseIcon />, color: classes.colorBlue, action: handleHoldOrder },
    ...(userCanResetOrder
      ? [{ label: 'Reset Order', icon: <ResetIcon />, color: classes.colorOrange, action: handleOpenResetOrder }]
      : []),
    ...((!isLoading && !result.isLoading && userCanCancelOrder)
      ? [{ label: 'Cancel Order', icon: <CancelIcon />, color: classes.colorRed, action: handleCancelOrder }]
      : []),
  ];

  return (
    <Fragment>
      <Card className={classes.root}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIosIcon />}
          className={`${classes.button} ${classes.backButton}`}
          onClick={() => history.push('/orders')}
          size="large"
        >
          Back
        </Button>
        <div className={classes.wrapper}>
          {actionButtons.map(({ label, icon, color, action }) => (
            <Button
              className={clsx(classes.button, color)}
              variant="outlined"
              startIcon={icon}
              onClick={action}
              size="large"
            >
              {label}
            </Button>
          ))}
        </div>
      </Card>

      {openReschedule && (
        <RescheduleOrder
          orderID={orderID}
          open={openReschedule}
          closeReschedule={handleCloseReschedule}
          refetchActivityHistory={refetchActivityHistory}
        />
      )}

      {openResetOrder && (
        <ResetOrder
          orderID={orderID}
          open={openResetOrder}
          closeResetOrder={handleCloseResetOrder}
          refetchActivityHistory={refetchActivityHistory}
          shopper={shopper}
        />
      )}

      {openChangeStore && (
        <ChangeStore
          orderID={orderID}
          open={openChangeStore}
          closeChangeStore={handleCloseChangeStore}
          refetchActivityHistory={refetchActivityHistory}
        />
      )}
    </Fragment>
  );
};

export default OrderActionButtons;
